import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { ClientProfile } from '../types';

interface Props {
  profile: ClientProfile;
  onUpdate: (data: Partial<ClientProfile>) => void;
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20';
const labelCls = 'block text-[11px] text-white/40 mb-1.5 font-500';

export default function PersonalTab({ profile, onUpdate }: Props) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    display_name: profile.display_name || '',
    phone: profile.phone || '',
    cpf: profile.cpf || '',
    cep: profile.cep || '',
    street: profile.street || '',
    street_number: profile.street_number || '',
    complement: profile.complement || '',
    neighborhood: profile.neighborhood || '',
    city: profile.city || '',
    state: profile.state || '',
    country: profile.country || 'BR',
  });

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const lookupCep = async () => {
    const raw = form.cep.replace(/\D/g, '');
    if (raw.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((f) => ({
          ...f,
          street: data.logradouro || f.street,
          neighborhood: data.bairro || f.neighborhood,
          city: data.localidade || f.city,
          state: data.uf || f.state,
          country: 'BR',
        }));
      }
    } catch (_) { /* ignore */ }
    setCepLoading(false);
  };

  const save = async () => {
    setSaving(true);
    const full_name = [form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(' ') || profile.full_name;
    const { error } = await supabase.from('profiles').update({
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim() || null,
      display_name: form.display_name.trim() || null,
      full_name,
      phone: form.phone.trim() || null,
      cpf: form.cpf.trim() || null,
      cep: form.cep.trim() || null,
      street: form.street.trim() || null,
      street_number: form.street_number.trim() || null,
      complement: form.complement.trim() || null,
      neighborhood: form.neighborhood.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      country: form.country.trim() || 'BR',
      updated_at: new Date().toISOString(),
    }).eq('id', profile.id);
    if (!error) {
      onUpdate({ ...form, full_name });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-3 gap-5">
      {/* Personal data + address */}
      <div className="col-span-2 space-y-5">
        {/* Identity */}
        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4 flex items-center gap-2">
            <i className="ri-user-line text-violet-400" />
            Dados Pessoais
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Nome</label>
              <input className={inputCls} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="Nome" />
            </div>
            <div>
              <label className={labelCls}>Sobrenome</label>
              <input className={inputCls} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Sobrenome" />
            </div>
            <div>
              <label className={labelCls}>Nome de exibição no app</label>
              <input className={inputCls} value={form.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="Como aparece no app" />
            </div>
            <div>
              <label className={labelCls}>Telefone / WhatsApp</label>
              <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(11) 99999-9999" />
            </div>
            <div>
              <label className={labelCls}>CPF</label>
              <input className={inputCls} value={form.cpf} onChange={(e) => set('cpf', e.target.value)} placeholder="000.000.000-00" />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-1 flex items-center gap-2">
            <i className="ri-map-pin-line text-violet-400" />
            Endereço
          </h3>
          <p className="text-[12px] text-white/30 mb-4">Digite o CEP e o endereço será preenchido automaticamente via ViaCEP</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>CEP</label>
              <div className="relative">
                <input
                  className={inputCls}
                  value={form.cep}
                  onChange={(e) => set('cep', e.target.value)}
                  onBlur={lookupCep}
                  placeholder="00000-000"
                  maxLength={9}
                />
                {cepLoading && (
                  <i className="ri-loader-4-line animate-spin text-violet-400 absolute right-3 top-1/2 -translate-y-1/2 text-[14px]" />
                )}
              </div>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Rua / Logradouro</label>
              <input className={inputCls} value={form.street} onChange={(e) => set('street', e.target.value)} placeholder="Rua, Av., Estrada..." />
            </div>
            <div>
              <label className={labelCls}>Número</label>
              <input className={inputCls} value={form.street_number} onChange={(e) => set('street_number', e.target.value)} placeholder="123" />
            </div>
            <div>
              <label className={labelCls}>Complemento</label>
              <input className={inputCls} value={form.complement} onChange={(e) => set('complement', e.target.value)} placeholder="Apto, Bloco, Sala..." />
            </div>
            <div>
              <label className={labelCls}>Bairro</label>
              <input className={inputCls} value={form.neighborhood} onChange={(e) => set('neighborhood', e.target.value)} placeholder="Bairro" />
            </div>
            <div>
              <label className={labelCls}>Cidade</label>
              <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Cidade" />
            </div>
            <div>
              <label className={labelCls}>Estado (UF)</label>
              <input className={inputCls} value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="SP" maxLength={2} />
            </div>
            <div>
              <label className={labelCls}>País</label>
              <input className={inputCls} value={form.country} onChange={(e) => set('country', e.target.value)} placeholder="BR" />
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
          {saved && (
            <span className="text-emerald-400 text-[12px] flex items-center gap-1.5">
              <i className="ri-check-line" />
              Dados salvos com sucesso!
            </span>
          )}
        </div>
      </div>

      {/* Auth info panel */}
      <div className="space-y-4">
        <div className="bg-white/5 border border-white/8 rounded-xl p-4">
          <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-3">Conta & Autenticação</h3>
          <div className="space-y-2.5 text-[13px]">
            {[
              { label: 'E-mail', val: profile.email || '—' },
              { label: 'Provedor', val: profile.auth_provider === 'google' ? 'Google OAuth' : 'E-mail/Senha' },
              { label: 'PIN ativo', val: profile.pin_enabled ? 'Sim' : 'Não' },
              { label: 'Onboarding', val: <span className={profile.wizard_completed ? 'text-emerald-400 font-600' : 'text-orange-400 font-600'}>{profile.wizard_completed ? 'Concluído' : 'Pendente'}</span> },
              { label: 'Status da conta', val: profile.account_status || '—' },
              { label: 'Criado em', val: new Date(profile.created_at).toLocaleDateString('pt-BR') },
              { label: 'Último acesso', val: profile.last_seen_at ? new Date(profile.last_seen_at).toLocaleDateString('pt-BR') : '—' },
            ].map((item, i) => (
              <div key={i} className="flex justify-between items-center gap-2">
                <span className="text-white/40 shrink-0">{item.label}</span>
                <span className="text-white font-500 text-right">{item.val as any}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white/5 border border-white/8 rounded-xl p-4">
          <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-2">ID Único do Cliente</h3>
          <p className="font-mono text-[10px] text-white/40 break-all select-all bg-white/3 rounded-lg px-2 py-1.5">{profile.id}</p>
        </div>

        {profile.full_name && (
          <div className="bg-white/5 border border-white/8 rounded-xl p-4">
            <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-2">Nome Completo Derivado</h3>
            <p className="text-[13px] text-white">
              {[form.first_name, form.last_name].filter(Boolean).join(' ') || profile.full_name}
            </p>
            <p className="text-[11px] text-white/30 mt-1">Gerado automaticamente a partir de nome + sobrenome</p>
          </div>
        )}
      </div>
    </div>
  );
}
